import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';

function formatDuration(seconds) {
  if (!seconds) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

const PAGE_SIZE = 50;

const SORTABLE_COLUMNS = [
  { key: 'title', label: 'Title' },
  { key: 'duration', label: 'Duration' },
  { key: 'isrc_count', label: 'ISRCs' },
  { key: 'release_count', label: 'Releases' }
];

function LibrarySearch({ onSelect }) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fingerprint, setFingerprint] = useState('any'); // 'any' | 'has' | 'missing'
  const [multiIsrc, setMultiIsrc] = useState(false);
  const [sort, setSort] = useState('title');
  const [dir, setDir] = useState('asc');
  const debounceRef = useRef();

  const runSearch = useCallback((params) => {
    setLoading(true);
    setError('');
    axios.get(`${API}/api/library/recordings`, { params })
      .then(res => {
        setRows(res.data.rows);
        setTotal(res.data.total);
      })
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setLoading(false));
  }, []);

  const currentParams = (pageNum = page) => ({
    q, limit: PAGE_SIZE, offset: pageNum * PAGE_SIZE,
    fingerprint, multiIsrc, sort, dir
  });

  useEffect(() => {
    runSearch(currentParams());
    // eslint-disable-next-line
  }, [page, fingerprint, multiIsrc, sort, dir]);

  const onQueryChange = (val) => {
    setQ(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(0);
      runSearch({ ...currentParams(0), q: val });
    }, 250);
  };

  const toggleSort = (col) => {
    if (sort === col) {
      setDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(col);
      // Numeric columns read more usefully starting high-to-low (most
      // releases, most ISRCs first); title stays alphabetical.
      setDir(col === 'title' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const goToPage = (p) => setPage(Math.max(0, Math.min(totalPages - 1, p)));

  const activeFilterCount = (fingerprint !== 'any' ? 1 : 0) + (multiIsrc ? 1 : 0);
  const clearFilters = () => {
    setFingerprint('any');
    setMultiIsrc(false);
    setPage(0);
  };

  return (
    <div>
      <Header>
        <div>
          <Eyebrow>Catalog</Eyebrow>
          <Title>Track Library</Title>
          <Subtitle>{total.toLocaleString()} recording{total === 1 ? '' : 's'} on file</Subtitle>
        </div>
      </Header>

      <SearchBar
        placeholder="Search by title, artist, or ISRC..."
        value={q}
        onChange={(e) => onQueryChange(e.target.value)}
      />

      <FilterRow>
        <FilterLabel>Filter</FilterLabel>
        <Chip active={multiIsrc} onClick={() => { setMultiIsrc(m => !m); setPage(0); }}>
          Multiple ISRCs
        </Chip>
        <Chip active={fingerprint === 'has'} onClick={() => { setFingerprint(f => f === 'has' ? 'any' : 'has'); setPage(0); }}>
          Has fingerprint
        </Chip>
        <Chip active={fingerprint === 'missing'} onClick={() => { setFingerprint(f => f === 'missing' ? 'any' : 'missing'); setPage(0); }}>
          No fingerprint
        </Chip>
        {activeFilterCount > 0 && (
          <ClearBtn onClick={clearFilters}>Clear filters</ClearBtn>
        )}
      </FilterRow>

      {error && <ErrorBox>{error}</ErrorBox>}

      <TableScroll>
        <Table>
          <thead>
            <tr>
              {SORTABLE_COLUMNS.map(col => (
                <SortableTh key={col.key} onClick={() => toggleSort(col.key)} active={sort === col.key}>
                  {col.label}
                  <SortArrow active={sort === col.key} dir={dir}>
                    {sort === col.key ? (dir === 'asc' ? '↑' : '↓') : ''}
                  </SortArrow>
                </SortableTh>
              ))}
              <th>Version</th>
              <th>Artist</th>
              <th>ISRC</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Row key={r.id} onClick={() => onSelect(r.id)}>
                <td>{r.title || <Muted>Untitled</Muted>}</td>
                <td><Mono>{formatDuration(r.duration_seconds)}</Mono></td>
                <td style={{ textAlign: 'center' }}>
                  {r.isrc_count > 1 ? <FlagBadge>{r.isrc_count}</FlagBadge> : r.isrc_count}
                </td>
                <td style={{ textAlign: 'center' }}>{r.release_count}</td>
                <td><Muted>{r.version || '—'}</Muted></td>
                <td>{r.primary_artist || <Muted>—</Muted>}</td>
                <td><Mono>{r.canonical_isrc || '—'}</Mono></td>
              </Row>
            ))}
          </tbody>
        </Table>
      </TableScroll>

      {!loading && rows.length === 0 && (
        <EmptyState>
          {q || activeFilterCount > 0 ? 'No recordings match these filters' : 'No recordings in the library'}
        </EmptyState>
      )}
      {loading && <EmptyState>Loading...</EmptyState>}

      {!loading && rows.length > 0 && (
        <Pagination>
          <PageBtn onClick={() => goToPage(page - 1)} disabled={page === 0}>← Prev</PageBtn>
          <PageInfo>
            Page {page + 1} of {totalPages} <Muted>({total.toLocaleString()} recordings)</Muted>
          </PageInfo>
          <PageBtn onClick={() => goToPage(page + 1)} disabled={page >= totalPages - 1}>Next →</PageBtn>
        </Pagination>
      )}
    </div>
  );
}

const Header = styled.div`
  margin-bottom: 20px;
`;

const Eyebrow = styled.p`
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--accent-text);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 26px;
  color: var(--text);
  letter-spacing: -0.3px;
`;

const Subtitle = styled.div`
  font-size: 12px;
  color: var(--text-2);
  margin-top: 4px;
`;

const SearchBar = styled.input`
  width: 100%;
  padding: 10px 14px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--bg-2);
  font-size: 13px;
  color: var(--text);
  margin-bottom: 14px;
  transition: border-color 0.15s;

  &:focus {
    border-color: var(--accent);
  }
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 18px;
`;

const FilterLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-3);
  margin-right: 4px;
`;

const Chip = styled.button`
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid ${p => p.active ? 'var(--accent)' : 'var(--border)'};
  background: ${p => p.active ? 'var(--accent-dim)' : 'var(--bg-2)'};
  color: ${p => p.active ? 'var(--accent-text)' : 'var(--text-2)'};
  transition: all 0.12s;
  &:hover { border-color: ${p => p.active ? 'var(--accent)' : 'var(--border-2)'}; }
`;

const ClearBtn = styled.button`
  font-size: 11.5px;
  color: var(--text-3);
  text-decoration: underline;
  padding: 6px 4px;
  &:hover { color: var(--text-2); }
`;

const TableScroll = styled.div`
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
`;

const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  background: var(--bg-2);

  thead th {
    text-align: left;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-3);
    font-weight: 500;
    padding: 9px 14px;
    border-bottom: 1px solid var(--border);
    background: var(--bg-3);
    white-space: nowrap;
  }

  tbody td {
    padding: 9px 14px;
    font-size: 13px;
    border-bottom: 1px solid var(--border);
  }

  tbody tr:last-child td { border-bottom: none; }
`;

const SortableTh = styled.th`
  cursor: pointer;
  user-select: none;
  color: ${p => p.active ? 'var(--accent-text)' : 'var(--text-3)'};
  &:hover { color: var(--text); }
`;

const SortArrow = styled.span`
  display: inline-block;
  width: 10px;
  margin-left: 2px;
  color: var(--accent);
`;

const Row = styled.tr`
  cursor: pointer;
  transition: background 0.1s;
  &:hover td { background: var(--bg-3); }
`;

const Mono = styled.span`
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-2);
`;

const Muted = styled.span`
  color: var(--text-3);
`;

const FlagBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--amber-dim);
  color: var(--amber);
  font-weight: 600;
  font-size: 11px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 18px;
`;

const PageBtn = styled.button`
  background: var(--bg-2);
  border: 1.5px solid var(--border);
  color: var(--text);
  font-weight: 700;
  font-size: 12px;
  padding: 8px 16px;
  border-radius: var(--radius);
  transition: all 0.15s;
  &:hover:not(:disabled) {
    border-color: var(--blue);
    color: var(--blue-dark);
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PageInfo = styled.div`
  font-size: 12px;
  color: var(--text-2);
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: var(--text-3);
  font-size: 13px;
`;

const ErrorBox = styled.div`
  background: var(--red-dim);
  color: var(--red);
  padding: 10px 14px;
  border-radius: var(--radius);
  margin-bottom: 14px;
  font-size: 13px;
`;

export default LibrarySearch;
